'use client';
import Script from 'next/script';
import Loading from '@/components/common/Loading';
import AppLogo from '@/components/common/AppLogo';
import { automaticReloginRequestedAtom } from '@/components/common/authentication/automaticRelogin';
import useSessionNotices, {
  LOGIN_FAILED,
  LOGIN_NOT_SAVED,
  UNREACHABLE,
} from '@/components/common/authentication/useSessionNotices';
import useProGloveScreen from '@/components/proglove/display/useProGloveScreen';
import { apiSdkAtom } from '@/context/atoms/ApiSdk';
import { authMechanismAtom } from '@/context/atoms/AuthMechanism';
import { sessionStateAtom } from '@/context/atoms/Session';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AuthStoreError, type Credentials, SessionUnavailableError } from '@ekkogmbh/apisdk';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WebEamAuthMechanism, WebEamLoginError } from '@/helper/auth/WebEamAuthMechanism';

export const dynamic = 'force-dynamic';

export default function LogIn(): React.JSX.Element {
  const api = useAtomValue(apiSdkAtom);
  const authMechanism = useAtomValue(authMechanismAtom);
  const session = useAtomValue(sessionStateAtom);
  const { notifyError, notifyWarning } = useSessionNotices();
  const [refInput, setRefInput] = useState<HTMLInputElement | null>(null);
  const isWebEam = authMechanism instanceof WebEamAuthMechanism;
  const [isReloginRequested, setReloginRequested] = useAtom(automaticReloginRequestedAtom);

  // With WebEAM the SSO flow starts on its own once `e2e.js` has loaded, and
  // after `AuthGuard` logged out an expired token. The library stays in the
  // page, so a loaded one without that request means this tab logged out and
  // the user signs in with the button.
  const [isLoggingIn, setLoggingIn] = useState<boolean>(
    () => isWebEam && (window.e2e === undefined || isReloginRequested),
  );
  const reloginStartedRef = useRef(false);

  // A WebEAM login needs nothing from the user until it fails, so the page
  // and the glove only say that it signs in.
  const isSigningInOnly = isWebEam && isLoggingIn;

  // Logins this page started that have not settled yet. While one is pending,
  // its rejection words the notice, and the session's 'login-failed' for it
  // shows none. A ref, as the session publishes inside `login()`, before a
  // state update would be visible.
  const pendingLoginsRef = useRef(0);

  useProGloveScreen({
    markDisplay: {
      template: 'PG1',
      fields: [{ header: 'ekko', text: isSigningInOnly ? 'Signing in…' : 'Please login to continue' }],
    },
  });

  useEffect(() => {
    refInput?.focus();
  }, [refInput]);

  // A WebEAM login reports its outcome after `login()` resolved, when the
  // library answers later: as 'login', which leaves this page, or as
  // 'login-failed'.
  useEffect(
    () =>
      api.session.subscribe((_state, reason) => {
        if (reason !== 'login-failed') {
          return;
        }

        setLoggingIn(false);
        if (pendingLoginsRef.current === 0) {
          notifyError(LOGIN_FAILED);
        }
      }),
    [api, notifyError],
  );

  const notifyLoginFailure = useCallback(
    (error: unknown): void => {
      if (error instanceof AuthStoreError) {
        notifyWarning(LOGIN_NOT_SAVED);
      } else if (error instanceof SessionUnavailableError) {
        notifyError(UNREACHABLE);
      } else if (error instanceof WebEamLoginError) {
        notifyError(`${LOGIN_FAILED}: ${error.message}`);
      } else {
        notifyError(LOGIN_FAILED);
      }
    },
    [notifyError, notifyWarning],
  );

  // With WebEAM a resolved `login()` only means that the flow started, so the
  // login counts as running until the session reports the outcome.
  const logIn = useCallback(
    async (credentials?: Credentials): Promise<void> => {
      setLoggingIn(true);
      pendingLoginsRef.current += 1;
      try {
        await api.session.login(credentials);
      } catch (error) {
        setLoggingIn(false);
        notifyLoginFailure(error);
      } finally {
        pendingLoginsRef.current -= 1;
      }
    },
    [api, notifyLoginFailure],
  );

  // React may run effects twice in development; the ref keeps it to one login.
  useEffect(() => {
    if (!isWebEam || !isReloginRequested || reloginStartedRef.current) {
      return;
    }

    reloginStartedRef.current = true;
    setReloginRequested(false);
    void logIn();
  }, [isWebEam, isReloginRequested, setReloginRequested, logIn]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (isWebEam) {
      void logIn();
      return;
    }

    const data = new FormData(event.currentTarget);
    const username = data.get('username');
    const password = data.get('password');

    if (typeof username === 'string' && typeof password === 'string') {
      void logIn({ username, password });
    }
  };

  if (session.isAuthenticated) {
    return <Loading size={150} />;
  }

  return (
    <Container component="main" maxWidth="xs">
      {isWebEam && (
        <Script
          src="auth/e2e.js"
          strategy="afterInteractive"
          onLoad={() => {
            if (!window.e2e) {
              setLoggingIn(false);
              throw new Error('E2E script not available');
            }
            authMechanism.setE2E(window.e2e);

            // Starts the SSO flow on page load. Another tab may have logged
            // this one in while the library loaded; a new flow would only
            // redirect away from the session the tab follows.
            if (!api.session.getState().isAuthenticated) {
              void logIn();
            }
          }}
          onError={() => {
            setLoggingIn(false);
            notifyError(`${LOGIN_FAILED}: The login service cannot be reached.`);
          }}
        />
      )}
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <AppLogo my={3} />
        {isSigningInOnly ? (
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="inherit" size={24} />
            <Typography>Signing in…</Typography>
          </Box>
        ) : (
          <Box component="form" action={'dialog'} onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            {!isWebEam && (
              <>
                <TextField
                  inputRef={setRefInput}
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                />
              </>
            )}
            <Button disabled={isLoggingIn} type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
              Sign In
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
}
